/**
 * Variant Selector Reset Solution
 * When a primary variant option (like Material) changes, 
 * reset dependent options so customers know which combinations are available
 */

class VariantSelectorReset {
  constructor() {
    this.variantSelects = document.querySelector('variant-selects');
    if (!this.variantSelects) return;
    
    this.fieldsets = this.variantSelects.querySelectorAll('fieldset');
    this.variantsData = this.parseVariants();
    this.hiddenInput = document.querySelector('input[name="id"]');
    this.productForm = document.querySelector('product-form');
    
    // Try to find price element, but don't fail if not found (we'll create it)
    this.priceElement = document.querySelector('.add-to-cart-price');
    
    // Store initial/default price if element exists
    this.defaultPrice = this.priceElement ? this.priceElement.textContent.trim() : null;
    
    this.init();
  }

  parseVariants() {
    const scriptTag = this.variantSelects.querySelector('script[type="application/json"]:not(.productVariantsQty)');
    if (!scriptTag) return [];
    try {
      return JSON.parse(scriptTag.textContent);
    } catch (e) {
      console.error('Failed to parse variants:', e);
      return [];
    }
  }

  init() {
    // Find or create price element first
    this.findOrCreatePriceElement();

    // Watch for button content changes (in case variant-selects replaces it)
    const addToCartButton = document.querySelector('.add-cart-button, .product-form__submit, button[type="submit"][name="add"]');
    if (addToCartButton) {
      const buttonObserver = new MutationObserver(() => {
        // If price element was removed, recreate it
        if (!document.querySelector('.add-to-cart-price')) {
          this.findOrCreatePriceElement();
          setTimeout(() => this.updatePrice(), 100);
        }
      });

      buttonObserver.observe(addToCartButton, {
        childList: true,
        subtree: true
      });
    }

    // Add change listeners to all option swatches
    this.fieldsets.forEach((fieldset, index) => {
      const options = fieldset.querySelectorAll('[data-value]');
      options.forEach(option => {
        option.addEventListener('click', () => {
          // Use setTimeout to ensure the variant-selects component has processed the change
          setTimeout(() => {
            this.handleOptionChange(index);
          }, 100);
        });
      });
    });

    // Watch for hidden input changes to update price
    if (this.hiddenInput) {
      // Use MutationObserver for attribute changes
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            setTimeout(() => this.updatePrice(), 100);
          }
        });
      });

      observer.observe(this.hiddenInput, {
        attributes: true,
        attributeFilter: ['value', 'disabled']
      });
      
      // Also listen for change events
      this.hiddenInput.addEventListener('change', () => {
        setTimeout(() => this.updatePrice(), 100);
      });

      // Listen for input events (when value is programmatically changed)
      this.hiddenInput.addEventListener('input', () => {
        setTimeout(() => this.updatePrice(), 100);
      });
    }

    // Listen to variant-selects component events if available
    if (this.variantSelects) {
      // Listen for custom events that variant-selects might dispatch
      this.variantSelects.addEventListener('change', () => {
        setTimeout(() => this.updatePrice(), 150);
      });

      // Also listen for variantchange event if it exists
      this.variantSelects.addEventListener('variantchange', () => {
        setTimeout(() => this.updatePrice(), 150);
      });
    }

    // Also listen to the product-form component
    if (this.productForm) {
      this.productForm.addEventListener('change', () => {
        setTimeout(() => this.updatePrice(), 150);
      });
    }

    // Listen to document-level events for variant changes
    document.addEventListener('variant:change', () => {
      setTimeout(() => this.updatePrice(), 150);
    });

    // Initial price update
    setTimeout(() => this.updatePrice(), 300);
  }

  handleOptionChange(changedFieldsetIndex) {
    const option1 = this.getSelectedOption(0); // Material
    const option2 = this.getSelectedOption(1); // Size

    // If the changed option is not the last one (Material changed)
    // Reset all dependent options
    if (changedFieldsetIndex < this.fieldsets.length - 1) {
      // Reset all options after the changed one
      for (let i = changedFieldsetIndex + 1; i < this.fieldsets.length; i++) {
        this.resetFieldset(i);
      }
    }

    // Update available options for remaining fieldsets
    this.updateAvailableOptions();
    
    // Update price immediately when Material changes (index 0)
    if (changedFieldsetIndex === 0) {
      // Update immediately
      this.updatePrice();
      // Also update after a short delay to catch any DOM updates
      setTimeout(() => this.updatePrice(), 100);
      setTimeout(() => this.updatePrice(), 300);
    } else {
      // For Size changes, wait a bit for variant-selects to process
      setTimeout(() => this.updatePrice(), 300);
    }
  }

  getSelectedOption(fieldsetIndex) {
    const fieldset = this.fieldsets[fieldsetIndex];
    if (!fieldset) return null;
    const selected = fieldset.querySelector('[data-value].active');
    return selected ? selected.getAttribute('data-value') : null;
  }

  resetFieldset(fieldsetIndex) {
    const fieldset = this.fieldsets[fieldsetIndex];
    if (!fieldset) return;

    // Remove active class from all options
    const options = fieldset.querySelectorAll('[data-value]');
    options.forEach(option => {
      option.classList.remove('active');
    });

    // Update the legend to show no selection
    const legend = fieldset.querySelector('.swatch-selected-value');
    if (legend) {
      legend.textContent = '—'; // Show a dash to indicate no selection
    }
  }

  updateAvailableOptions() {
    const option1 = this.getSelectedOption(0); // Material

    // Update Size availability based on Material
    if (option1) {
      this.updateFieldsetAvailability(1, option1, null);
    }
  }

  updateFieldsetAvailability(fieldsetIndex, option1Value, option2Value) {
    const fieldset = this.fieldsets[fieldsetIndex];
    if (!fieldset) return;

    const options = fieldset.querySelectorAll('[data-value]');

    options.forEach(option => {
      const optionValue = option.getAttribute('data-value');
      const isAvailable = this.isVariantAvailable(option1Value, optionValue, fieldsetIndex);

      if (isAvailable) {
        option.removeAttribute('data-disabled');
        option.style.opacity = '1';
        option.style.pointerEvents = 'auto';
        option.style.cursor = 'pointer';
      } else {
        option.setAttribute('data-disabled', 'disable');
        option.style.opacity = '0.5';
        option.style.pointerEvents = 'none';
        option.style.cursor = 'not-allowed';
      }
    });
  }

  isVariantAvailable(option1, optionValue, fieldsetIndex) {
    return this.variantsData.some(variant => {
      // Check if this variant matches the criteria
      if (variant.option1 !== option1) return false;

      if (fieldsetIndex === 1) {
        // Checking Size availability (option2 in your case)
        return variant.option2 === optionValue && variant.available;
      }

      return false;
    });
  }

  // Get the first available variant for a selected option1 (Material)
  // by looping through all available option2 (Size) values
  getFirstAvailableVariantForOption1(option1Value) {
    if (!option1Value || !this.fieldsets || this.fieldsets.length < 2) {
      return null;
    }

    // Get the second fieldset (Size options)
    const sizeFieldset = this.fieldsets[1];
    if (!sizeFieldset) {
      return null;
    }

    // Get all Size option values from the fieldset
    const sizeOptions = sizeFieldset.querySelectorAll('[data-value]');
    const sizeValues = Array.from(sizeOptions).map(opt => opt.getAttribute('data-value'));

    // Loop through Size values in order and find the first available variant
    for (const sizeValue of sizeValues) {
      const variant = this.variantsData.find(v => 
        v.option1 === option1Value && 
        v.option2 === sizeValue && 
        v.available === true
      );
      
      if (variant) {
        return variant;
      }
    }

    // If no available variant found, return null
    return null;
  }

  // IMPROVED METHOD: Update price in the Add to Cart button
  updatePrice() {
    // Find or create the price element
    this.findOrCreatePriceElement();
    
    if (!this.priceElement) {
      console.warn('Could not find or create price element');
      return;
    }

    // Ensure the price element is visible
    if (this.priceElement.parentElement) {
      this.priceElement.parentElement.style.display = '';
    }
    this.priceElement.style.display = '';

    let selectedVariant = null;
    const option1 = this.getSelectedOption(0); // Material
    const option2 = this.getSelectedOption(1); // Size

    // PRIORITY 1: Check selected options first (this ensures we get the right price when Material changes)
    if (option1 && option2) {
      // Both options selected - find the exact matching variant
      selectedVariant = this.variantsData.find(v => 
        v.option1 === option1 && v.option2 === option2 && v.available
      );
    } else if (option1) {
      // Only Material selected - find first available variant for that Material
      // by looping through available Size values
      selectedVariant = this.getFirstAvailableVariantForOption1(option1);
    }

    // PRIORITY 2: If no options selected, try to get variant ID from hidden input
    if (!selectedVariant && this.hiddenInput && !this.hiddenInput.disabled) {
      const inputValue = this.hiddenInput.value;
      if (inputValue) {
        const variantId = parseInt(inputValue);
        selectedVariant = this.variantsData.find(v => v.id === variantId);
      }
    }

    // If still no variant, use first available variant as fallback
    if (!selectedVariant) {
      selectedVariant = this.variantsData.find(v => v.available) || this.variantsData[0];
    }

    // Update the price
    if (selectedVariant && selectedVariant.price) {
      const formattedPrice = this.formatMoney(selectedVariant.price);
      this.priceElement.textContent = '- ' + formattedPrice;
    } else if (this.defaultPrice) {
      // Fallback to default price if available
      this.priceElement.textContent = this.defaultPrice;
    } else {
      // Last resort: try to get price from the product JSON
      const productJson = document.querySelector('script[data-product-json]');
      if (productJson) {
        try {
          const productData = JSON.parse(productJson.textContent);
          if (productData.variants && productData.variants.length > 0) {
            const firstVariant = productData.variants.find(v => v.available) || productData.variants[0];
            if (firstVariant && firstVariant.price) {
              const formattedPrice = this.formatMoney(firstVariant.price);
              this.priceElement.textContent = '- ' + formattedPrice;
            }
          }
        } catch (e) {
          console.error('Failed to parse product JSON:', e);
        }
      }
    }
  }

  // Find or create the price element
  findOrCreatePriceElement() {
    // First, try to find existing price element
    this.priceElement = document.querySelector('.add-to-cart-price');
    
    if (this.priceElement) {
      return;
    }

    // If not found, find the button and create the price element
    const addToCartButton = document.querySelector('.add-cart-button, .product-form__submit, button[type="submit"][name="add"]');
    
    if (!addToCartButton) {
      console.warn('Add to cart button not found');
      return;
    }

    // Find the hidden-loading span inside the button
    const hiddenLoadingSpan = addToCartButton.querySelector('.hidden-loading');
    
    if (!hiddenLoadingSpan) {
      console.warn('hidden-loading span not found in button');
      return;
    }

    // Create the price element
    this.priceElement = document.createElement('span');
    this.priceElement.className = 'add-to-cart-price';
    
    // Add a space before the price element
    hiddenLoadingSpan.appendChild(document.createTextNode(' '));
    
    // Insert it after the button text (or at the end of hidden-loading span)
    hiddenLoadingSpan.appendChild(this.priceElement);
    
    // Store default price if we have one
    if (!this.defaultPrice) {
      // Try to get initial price from product JSON
      const productJson = document.querySelector('script[data-product-json]');
      if (productJson) {
        try {
          const productData = JSON.parse(productJson.textContent);
          if (productData.variants && productData.variants.length > 0) {
            const firstVariant = productData.variants.find(v => v.available) || productData.variants[0];
            if (firstVariant && firstVariant.price) {
              const formattedPrice = this.formatMoney(firstVariant.price);
              this.defaultPrice = '- ' + formattedPrice;
            }
          }
        } catch (e) {
          console.error('Failed to parse product JSON for default price:', e);
        }
      }
    }
  }

  // Format money helper
  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme_money_format || 'LE {{amount}}');
    }
    // Fallback formatting for Egyptian Pounds
    const amount = (cents / 100).toFixed(2);
    return 'LE ' + amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new VariantSelectorReset();
  });
} else {
  new VariantSelectorReset();
}

